import React, {
    useState,
    useRef,
    useCallback,
    useMemo,
    useContext,
} from 'react'
import styled from '@emotion/styled'
import { ChevronDown, ChevronUp } from 'lucide-react'
import * as Slider from '@radix-ui/react-slider'
import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import {
    Dialog,
    DialogTrigger,
    DialogOverlay,
    DialogClose,
    DialogHandle,
} from './Dialog'
import {
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_CTA_FILL_COLOR_VAR,
    PRIMARY_CTA_TEXT_COLOR_VAR,
    PRIMARY_CTA_BORDER_VAR,
    SECONDARY_FONT_CSS_VAR,
    CustomizationContext,
} from '../../context/CustomizationContextProvider'
import { getTypographyRoleStyle } from '../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../style/typographySizes'
import { ProductFilterButton } from './ProductFilterButton'
import { FilterPill } from './FilterPills'
import { ViddyLPContext } from '../../context/viddyLPContext'
import { AnalyticsContext } from '../../context/analyticsContext'
import { getLinkPrices } from '../../utils/priceFormat'
import type { ApiPromotion } from '@storefronts/common-types'
import { scaledFontSize } from '../../style/scrollingLayout'
import { dialogSheet } from './overlayModal'

/**
 * Checks if a product has any available variants for the displayed product.
 * For virtual products, only checks variants belonging to the displayed product
 * (matching shopifyProductId), since users can't switch colors in the grid view.
 * Falls back to checking all variants if no variants match the displayed product.
 */
const hasAvailableDisplayedVariant = (
    product: ApiShopperLandingPageLink,
): boolean | undefined => {
    if (!product.variantInfo?.length) return undefined

    const displayedVariants = product.variantInfo.filter(
        (variant) =>
            !variant.shopifyProductId ||
            variant.shopifyProductId === product.shopifyId,
    )

    if (displayedVariants.length > 0) {
        return displayedVariants.some((variant) => variant.available)
    }

    return product.variantInfo.some((variant) => variant.available)
}

const FilterDialogContent = styled.form({
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    borderRadius: '15px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    ...dialogSheet,
})

const FilterHeader = styled.div({
    padding: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
})

const FilterTitle = styled.h2({
    ...getTypographyRoleStyle('headingText', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: '600',
    }),
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.SECTION),
    margin: 0,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
})

const ScrollableContent = styled.div({
    margin: '8px 0px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
    padding: '0 16px',
})

const FilterSectionContainer = styled.div({
    marginBottom: '16px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    paddingBottom: '16px',
})

const FilterSectionHeader = styled.div({
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    ...getTypographyRoleStyle('labelText', {
        fontWeight: 500,
    }),
    fontSize: scaledFontSize(16),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    textAlign: 'left',
    '&:focus': {
        outline: 'none',
    },
})

const FilterSectionContent = styled.div<{ isExpanded: boolean }>`
    display: ${(props) => (props.isExpanded ? 'block' : 'none')};
`

const FilterOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 12px;
    padding-bottom: 8px;
`
const CheckboxOptionContainer = styled.label`
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    padding: 0;
    user-select: none;
`

const CheckboxInput = styled.input`
    width: 17px;
    height: 17px;
    cursor: pointer;
    accent-color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    flex-shrink: 0;
`

const CheckboxLabel = styled.span({
    ...getTypographyRoleStyle('bodyText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(12),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    flex: 1,
})

const PriceInputsContainer = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
`

const PriceInputWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const PriceInputLabel = styled.label({
    ...getTypographyRoleStyle('labelText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(12),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
})

const PriceInputContainer = styled.div`
    display: flex;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 6px;
    background: var(${PRIMARY_PAGE_FILL_COLOR_VAR});

    &:focus-within {
        border-color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    }
`

const PriceInputPrefix = styled.span({
    padding: '8px 0 8px 12px',
    ...getTypographyRoleStyle('bodyText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(14),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    userSelect: 'none',
})

const PriceInput = styled.input({
    flex: 1,
    padding: '8px 12px 8px 4px',
    border: 'none',
    borderRadius: '6px',
    ...getTypographyRoleStyle('bodyText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(14),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    background: 'transparent',
    '&:focus': {
        outline: 'none',
    },
})

const SliderRoot = styled(Slider.Root)`
    position: relative;
    display: flex;
    align-items: center;
    user-select: none;
    touch-action: none;
    width: 100%;
    height: 20px;
`

const SliderTrack = styled(Slider.Track)`
    background-color: rgba(0, 0, 0, 0.1);
    position: relative;
    flex-grow: 1;
    border-radius: 9999px;
    height: 4px;
`

const SliderRange = styled(Slider.Range)`
    position: absolute;
    background-color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    border-radius: 9999px;
    height: 100%;
`

const SliderThumb = styled(Slider.Thumb)`
    display: block;
    width: 16px;
    height: 16px;
    background-color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    border-radius: 50%;
    cursor: grab;

    &:focus {
        outline: none;
        box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.1);
    }

    &:active {
        cursor: grabbing;
    }
`

const FilterActions = styled.div({
    display: 'flex',
    flexDirection: 'row-reverse',
    gap: '12px',
    padding: '16px',
    borderTop: `1px solid rgba(0, 0, 0, 0.1)`,
})

const ActionButton = styled.button({
    flex: 1,
    padding: 14,
    borderRadius: 8,
    ...getTypographyRoleStyle('buttonText', {
        fontWeight: 500,
    }),
    fontSize: scaledFontSize(16),
    cursor: 'pointer',
    border: 'none',
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    '&:focus': {
        outline: 'none',
    },
})

const ClearButton = styled(ActionButton)`
    background: transparent;
    color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    border: 1px solid rgba(0, 0, 0, 0.2);

    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
`

const ApplyButton = styled(ActionButton)`
    background: var(${PRIMARY_CTA_FILL_COLOR_VAR});
    color: var(${PRIMARY_CTA_TEXT_COLOR_VAR});
    border: var(${PRIMARY_CTA_BORDER_VAR});
    box-shadow: rgba(0, 0, 0, 0.3) 0px 2px 10px;

    &:hover {
        opacity: 0.9;
    }
`

const FilterSection: React.FC<{
    name: string
    children: React.ReactNode
    startExpanded?: boolean
}> = ({ name, children, startExpanded = false }) => {
    const [isExpanded, setIsExpanded] = useState(startExpanded)

    return (
        <FilterSectionContainer>
            <FilterSectionHeader onClick={() => setIsExpanded(!isExpanded)}>
                <span>{name}</span>
                {isExpanded ? (
                    <ChevronUp size={20} />
                ) : (
                    <ChevronDown size={20} />
                )}
            </FilterSectionHeader>
            <FilterSectionContent isExpanded={isExpanded}>
                {children}
            </FilterSectionContent>
        </FilterSectionContainer>
    )
}

type FilterableOptions = {
    [name: string]: string[]
}

function useFilterOptions(
    products: ApiShopperLandingPageLink[],
): FilterableOptions {
    return useMemo(() => {
        const options = new Map<string, Set<string>>()
        products.forEach((product) => {
            product.optionInfo?.forEach((option) => {
                if (
                    ['size', 'color', 'colour'].includes(
                        option.name.toLowerCase(),
                    )
                ) {
                    if (!options.has(option.name)) {
                        options.set(
                            option.name,
                            new Set<string>(
                                option.values.map(({ value }) => value),
                            ),
                        )
                    } else {
                        const existingValues = options.get(option.name)
                        option.values.forEach(({ value }) => {
                            existingValues?.add(value)
                        })
                    }
                }
            })
        })
        return Object.fromEntries(
            Array.from(options.entries()).map(([name, values]) => [
                name,
                Array.from(values),
            ]),
        )
    }, [products])
}

type PriceRange = {
    min: number
    max: number
}

type FilterState = {
    selectedFilters: FilterableOptions
    setSelectedFilters: (filters: FilterableOptions) => void
    removeFilter: (optionName: string, value: string) => void
    isFilterActive: (optionName: string, value: string) => boolean
    isFilterSectionActive: (optionName: string) => boolean
    priceRange?: PriceRange
    setPriceRange: (range: PriceRange) => void
    isPriceFilterActive: () => boolean
    removePriceFilter: () => void
    getFilteredProducts: (
        products: ApiShopperLandingPageLink[],
        discount?: ApiPromotion,
    ) => ApiShopperLandingPageLink[]
}

function useFilterState(): FilterState {
    const { hideUnavailableProducts } = useContext(CustomizationContext)
    const [selectedFilters, setSelectedFilters] = useState<FilterableOptions>(
        {},
    )
    const [priceRange, setPriceRange] = useState<PriceRange>()

    const isFilterActive = useCallback(
        (optionName: string, value: string): boolean => {
            return selectedFilters[optionName]?.includes(value) ?? false
        },
        [selectedFilters],
    )

    const isFilterSectionActive = useCallback(
        (optionName: string): boolean => {
            return selectedFilters[optionName]?.length > 0
        },
        [selectedFilters],
    )

    const isPriceFilterActive = useCallback((): boolean => {
        return Boolean(priceRange)
    }, [priceRange])

    const removeFilter = useCallback(
        (optionName: string, value: string): void => {
            setSelectedFilters((prevFilters) => {
                if (!prevFilters[optionName]) return prevFilters
                const newFilters = { ...prevFilters }
                newFilters[optionName] = newFilters[optionName].filter(
                    (v) => v !== value,
                )
                if (newFilters[optionName].length === 0) {
                    delete newFilters[optionName]
                }
                return newFilters
            })
        },
        [],
    )

    const removePriceFilter = useCallback(() => {
        setPriceRange(undefined)
    }, [])

    const getFilteredProducts = useCallback(
        (
            products: ApiShopperLandingPageLink[],
            discount?: ApiPromotion,
        ): ApiShopperLandingPageLink[] => {
            return products.filter((product) => {
                // Apply global hide unavailable filter first
                if (hideUnavailableProducts) {
                    const isAvailable = hasAvailableDisplayedVariant(product)
                    // undefined means no variant info - don't filter (may not be synced)
                    if (isAvailable === false) {
                        return false
                    }
                }

                // Apply price filter if set
                if (priceRange) {
                    const productPrice = getLinkPrices(
                        product,
                        discount ?? null,
                    ).price
                    if (
                        productPrice < priceRange.min ||
                        productPrice > priceRange.max
                    ) {
                        return false
                    }
                }

                // Check every filter group applied
                return Object.entries(selectedFilters).every(
                    ([name, values]) => {
                        switch (name) {
                            case 'Availability':
                                // Skip when global filter is active (already handles this)
                                if (hideUnavailableProducts) return true

                                // Check availability using original logic (not the helper)
                                // to preserve behavior: products without variantInfo
                                // should appear as "Out of Stock"
                                const productIsAvailable =
                                    product.variantInfo?.some(
                                        (variant) => variant.available,
                                    )
                                return values.some((value) => {
                                    if (value === 'Available') {
                                        return productIsAvailable
                                    }
                                    if (value === 'Out of Stock') {
                                        return !productIsAvailable
                                    }
                                    return false
                                })
                            default:
                                // For all other filter groups, check if any of the selected filters match the product
                                return values.some((value) => {
                                    return (
                                        product.optionInfo
                                            ?.find(
                                                (option) =>
                                                    option.name === name,
                                            )
                                            ?.values?.some(
                                                ({ value: optionValue }) =>
                                                    optionValue === value,
                                            ) ?? false
                                    )
                                })
                        }
                    },
                )
            })
        },
        [selectedFilters, priceRange, hideUnavailableProducts],
    )

    return {
        selectedFilters,
        isFilterActive,
        isFilterSectionActive,
        setSelectedFilters,
        removeFilter,
        priceRange,
        setPriceRange,
        isPriceFilterActive,
        removePriceFilter,
        getFilteredProducts,
    }
}

const PillsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0;
    margin-top: 8px;
    margin-bottom: 0;
`

const ProductFilterPills: React.FC<{
    activeFilters: FilterableOptions
    removeFilter: (optionName: string, value: string) => void
    priceRange?: PriceRange
    removePriceFilter: () => void
    currencySymbol?: string
}> = ({
    activeFilters,
    removeFilter,
    priceRange,
    removePriceFilter,
    currencySymbol = '$',
}) => {
    const { hideUnavailableProducts } = useContext(CustomizationContext)

    return (
        <PillsContainer>
            {Object.entries(activeFilters)
                .filter(
                    ([name]) =>
                        !(hideUnavailableProducts && name === 'Availability'),
                )
                .flatMap(([name, values]) =>
                    values.map((value) => (
                        <FilterPill
                            key={`${name}-${value}`}
                            filter={value}
                            onFilterRemove={() => removeFilter(name, value)}
                        />
                    )),
                )}
            {priceRange && (
                <FilterPill
                    filter={`${currencySymbol}${Math.floor(priceRange.min / 100)} - ${currencySymbol}${Math.ceil(priceRange.max / 100)}`}
                    onFilterRemove={() => removePriceFilter()}
                />
            )}
        </PillsContainer>
    )
}

const PriceRangeControls: React.FC<{
    currentMinPriceDollars?: number
    currentMaxPriceDollars?: number
    initialMinPriceDollars: number
    initialMaxPriceDollars: number
    currencySymbol: string
}> = ({
    currentMinPriceDollars,
    currentMaxPriceDollars,
    initialMinPriceDollars,
    initialMaxPriceDollars,
    currencySymbol,
}) => {
    const resolvedMinPrice = currentMinPriceDollars ?? initialMinPriceDollars
    const resolvedMaxPrice = currentMaxPriceDollars ?? initialMaxPriceDollars

    const [localMinPrice, setLocalMinPrice] = useState<number>(resolvedMinPrice)
    const [localMaxPrice, setLocalMaxPrice] = useState<number>(resolvedMaxPrice)

    // Resync local drafts whenever the resolved price range changes upstream,
    // overriding any in-progress edit.
    const [prevResolvedMinPrice, setPrevResolvedMinPrice] =
        useState(resolvedMinPrice)
    const [prevResolvedMaxPrice, setPrevResolvedMaxPrice] =
        useState(resolvedMaxPrice)
    if (
        resolvedMinPrice !== prevResolvedMinPrice ||
        resolvedMaxPrice !== prevResolvedMaxPrice
    ) {
        setPrevResolvedMinPrice(resolvedMinPrice)
        setPrevResolvedMaxPrice(resolvedMaxPrice)
        setLocalMinPrice(resolvedMinPrice)
        setLocalMaxPrice(resolvedMaxPrice)
    }

    const handlePriceSliderChange = useCallback((values: number[]) => {
        const [min, max] = values
        setLocalMinPrice(min)
        setLocalMaxPrice(max)
    }, [])

    const handleMinPriceInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const dollarValue = parseInt(e.target.value)
            if (isNaN(dollarValue)) setLocalMinPrice(initialMinPriceDollars)
            else setLocalMinPrice(dollarValue)
        },
        [initialMinPriceDollars],
    )

    const handleMaxPriceInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const dollarValue = parseInt(e.target.value)
            if (isNaN(dollarValue)) setLocalMaxPrice(initialMaxPriceDollars)
            else setLocalMaxPrice(dollarValue)
        },
        [initialMaxPriceDollars],
    )

    return (
        <FilterOptionsContainer>
            <PriceInputsContainer>
                <PriceInputWrapper>
                    <PriceInputLabel>Min Price</PriceInputLabel>
                    <PriceInputContainer>
                        <PriceInputPrefix>{currencySymbol}</PriceInputPrefix>
                        <PriceInput
                            type="number"
                            name="minPrice"
                            value={localMinPrice}
                            onChange={handleMinPriceInputChange}
                            placeholder={String(initialMinPriceDollars)}
                            min={initialMinPriceDollars}
                            max={initialMaxPriceDollars}
                        />
                    </PriceInputContainer>
                </PriceInputWrapper>
                <PriceInputWrapper>
                    <PriceInputLabel>Max Price</PriceInputLabel>
                    <PriceInputContainer>
                        <PriceInputPrefix>{currencySymbol}</PriceInputPrefix>
                        <PriceInput
                            type="number"
                            name="maxPrice"
                            value={localMaxPrice}
                            onChange={handleMaxPriceInputChange}
                            placeholder={String(initialMaxPriceDollars)}
                            min={initialMinPriceDollars}
                            max={initialMaxPriceDollars}
                        />
                    </PriceInputContainer>
                </PriceInputWrapper>
            </PriceInputsContainer>
            <SliderRoot
                value={[localMinPrice, localMaxPrice]}
                onValueChange={handlePriceSliderChange}
                min={initialMinPriceDollars}
                max={initialMaxPriceDollars}
                step={1}
            >
                <SliderTrack>
                    <SliderRange />
                </SliderTrack>
                <SliderThumb />
                <SliderThumb />
            </SliderRoot>
        </FilterOptionsContainer>
    )
}

interface ProductFilterDialogProps {
    filterState: FilterState
    products?: ApiShopperLandingPageLink[]
}

const ProductFilterDialog: React.FC<ProductFilterDialogProps> = ({
    filterState,
    products = [],
}) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { hideUnavailableProducts } = useContext(CustomizationContext)
    const allFilters = useFilterOptions(products)
    const dialogRef = useRef<DialogHandle>(null)
    const discount = landingPage?.discount || null
    const priceRange = useMemo(() => {
        if (products.length === 0) return null

        const prices = products
            .map((product) => getLinkPrices(product, discount).price)
            .filter((price) => price > 0)

        if (prices.length === 0) return null

        const minPrice = Math.min(...prices)
        const maxPrice = Math.max(...prices)
        return {
            min: Math.floor(minPrice),
            max: Math.ceil(maxPrice),
        }
    }, [products, discount])

    const priceRangeDollars = useMemo(() => {
        if (!priceRange) return null
        return {
            min: Math.floor(priceRange.min / 100),
            max: Math.ceil(priceRange.max / 100),
        }
    }, [priceRange])

    const currencySymbol = landingPage?.client?.currencySymbol || '$'

    const currentMinPriceDollars = useMemo(() => {
        return filterState.priceRange?.min !== undefined
            ? Math.floor(filterState.priceRange.min / 100)
            : undefined
    }, [filterState.priceRange?.min])

    const currentMaxPriceDollars = useMemo(() => {
        return filterState.priceRange?.max !== undefined
            ? Math.ceil(filterState.priceRange.max / 100)
            : undefined
    }, [filterState.priceRange?.max])

    const handleSubmit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            Analytics.TRACK_PRODUCT_FILTER_SHOW_RESULTS_CLICK()
            const formData = new FormData(e.currentTarget)
            const newActiveFilters: FilterableOptions = {}

            if (priceRange) {
                // Extract and parse price values from form (in dollars)
                let minPriceDollars: number | undefined
                const formMinPrice = formData.get('minPrice')
                if (typeof formMinPrice === 'string') {
                    minPriceDollars = parseInt(formMinPrice)
                }

                let maxPriceDollars: number | undefined
                const formMaxPrice = formData.get('maxPrice')
                if (typeof formMaxPrice === 'string') {
                    maxPriceDollars = parseInt(formMaxPrice)
                }

                // Validate price values
                const isValidMin =
                    typeof minPriceDollars === 'number' &&
                    !isNaN(minPriceDollars)
                const isValidMax =
                    typeof maxPriceDollars === 'number' &&
                    !isNaN(maxPriceDollars)
                const isValidRange =
                    isValidMin &&
                    isValidMax &&
                    minPriceDollars! <= maxPriceDollars!

                if (isValidRange && priceRangeDollars) {
                    // Only apply filter if prices differ from initial range (compare in dollars)
                    const hasPriceChanged =
                        minPriceDollars! !== priceRangeDollars.min ||
                        maxPriceDollars! !== priceRangeDollars.max

                    if (hasPriceChanged) {
                        // Convert dollars to cents for storage
                        const minPriceCents = minPriceDollars! * 100
                        const maxPriceCents = maxPriceDollars! * 100
                        filterState.setPriceRange({
                            min: minPriceCents,
                            max: maxPriceCents,
                        })
                    } else {
                        // Prices match initial range, remove filter
                        filterState.removePriceFilter()
                    }
                } else {
                    filterState.removePriceFilter()
                }
            }

            formData.delete('minPrice')
            formData.delete('maxPrice')
            formData.forEach((value, name) => {
                if (typeof value !== 'string') return
                const filterOption = newActiveFilters[name]
                if (filterOption) {
                    filterOption.push(value)
                } else {
                    newActiveFilters[name] = [value]
                }
            })

            filterState.setSelectedFilters(newActiveFilters)
            dialogRef.current?.close()
        },
        [
            dialogRef,
            filterState.setSelectedFilters,
            filterState.setPriceRange,
            filterState.removePriceFilter,
            priceRange,
            priceRangeDollars,
            Analytics,
        ],
    )

    const handleClear = useCallback(() => {
        Analytics.TRACK_PRODUCT_FILTER_CLEAR_FILTERS_CLICK()
        filterState.setSelectedFilters({})
        filterState.removePriceFilter()
        dialogRef.current?.close()
    }, [
        dialogRef,
        filterState.setSelectedFilters,
        filterState.removePriceFilter,
        Analytics,
    ])

    return (
        <Dialog ref={dialogRef}>
            <DialogTrigger asChild>
                <ProductFilterButton
                    onClick={() => {
                        Analytics.TRACK_PRODUCT_FILTER_BUTTON_CLICK()
                    }}
                />
            </DialogTrigger>
            <DialogOverlay>
                <FilterDialogContent onSubmit={handleSubmit}>
                    <FilterHeader>
                        <FilterTitle>Filters</FilterTitle>
                        <DialogClose />
                    </FilterHeader>
                    <ScrollableContent>
                        {/* Availability filter hidden when global setting is enabled (redundant) */}
                        {!hideUnavailableProducts && (
                            <FilterSection
                                name="Availability"
                                startExpanded={filterState.isFilterSectionActive(
                                    'Availability',
                                )}
                            >
                                <FilterOptionsContainer>
                                    <CheckboxOptionContainer>
                                        <CheckboxInput
                                            type="checkbox"
                                            name="Availability"
                                            value="Available"
                                            defaultChecked={filterState.isFilterActive(
                                                'Availability',
                                                'Available',
                                            )}
                                        />
                                        <CheckboxLabel>Available</CheckboxLabel>
                                    </CheckboxOptionContainer>
                                    <CheckboxOptionContainer>
                                        <CheckboxInput
                                            type="checkbox"
                                            name="Availability"
                                            value="Out of Stock"
                                            defaultChecked={filterState.isFilterActive(
                                                'Availability',
                                                'Out of Stock',
                                            )}
                                        />
                                        <CheckboxLabel>
                                            Out of Stock
                                        </CheckboxLabel>
                                    </CheckboxOptionContainer>
                                </FilterOptionsContainer>
                            </FilterSection>
                        )}

                        {/* Variant Options Sections */}
                        {Object.entries(allFilters).map(([name, values]) => (
                            <FilterSection
                                key={name}
                                name={name}
                                startExpanded={filterState.isFilterSectionActive(
                                    name,
                                )}
                            >
                                <FilterOptionsContainer>
                                    {values.map((value) => (
                                        <CheckboxOptionContainer key={value}>
                                            <CheckboxInput
                                                type="checkbox"
                                                name={name}
                                                value={value}
                                                defaultChecked={filterState.isFilterActive(
                                                    name,
                                                    value,
                                                )}
                                            />
                                            <CheckboxLabel>
                                                {value}
                                            </CheckboxLabel>
                                        </CheckboxOptionContainer>
                                    ))}
                                </FilterOptionsContainer>
                            </FilterSection>
                        ))}

                        {priceRange && priceRangeDollars && (
                            <FilterSection
                                name="Price"
                                startExpanded={filterState.isPriceFilterActive()}
                            >
                                <PriceRangeControls
                                    currentMinPriceDollars={
                                        currentMinPriceDollars
                                    }
                                    currentMaxPriceDollars={
                                        currentMaxPriceDollars
                                    }
                                    initialMinPriceDollars={
                                        priceRangeDollars.min
                                    }
                                    initialMaxPriceDollars={
                                        priceRangeDollars.max
                                    }
                                    currencySymbol={currencySymbol}
                                />
                            </FilterSection>
                        )}
                    </ScrollableContent>
                    <FilterActions>
                        <ApplyButton type="submit">Show Results</ApplyButton>
                        <ClearButton type="button" onClick={handleClear}>
                            Clear Filters
                        </ClearButton>
                    </FilterActions>
                </FilterDialogContent>
            </DialogOverlay>
        </Dialog>
    )
}

export {
    ProductFilterDialog,
    useFilterState,
    useFilterOptions,
    ProductFilterPills,
    type FilterState,
}
